import * as momentTz from 'moment-timezone';
import { Component, Input, Output, Inject, EventEmitter, OnInit } from 'ng-metadata/core';
import { DateRangeService } from '../date-range.service'
import './date-range-menu.component.scss';

/**
 * @ngdoc directive
 * @name mwDateRangeMenu
 * @module mw.components.dateRange
 *
 * @param {Object} [date-range] - A selected date range option.  If provided, this will show a checkmark next to the appropriate date range option in the menu.  The date range option object should have the following structure, {name: string, type: string, startDate: Date, endDate: Date}.
 * @param {string|Object[]} [date-range-types] - Array of Date Range type options to display in the menu.  Supported values include a set of predefined types or custom Date Range option object.  Predefined types include 'today', 'thisWeek', 'last7Days', 'last14Days', 'thisMonth', 'last30Days', 'last90Days', 'thisQuarter', 'lastQuarter', 'thisYear', 'lastYear' and 'custom'.  (Default: =['today','thisWeek','last7Days', 'last14Days','thisMonth','last30Days', 'divider', 'custom']).  The custom date range option object should have the following structure, {name: string, type: string, startDate?: Date, endDate?: Date}.
 * @param {Date} [relative-date=new Date()] - The date that all relative date range type options will be relative to.
 * @param {boolean} [show-icon] - Determines whether or not to show a icon in front of the menu
 * @param {string} [trigger-date-format] - The format of the selected start and end date in the menu trigger button.  This format is used when both start and end dates are in the current year.
 * @param {string} [trigger-date-format-with-year] - The format of the selected start and end date in the menu trigger button, including year.  This format is used when start or end date are not in the current year.
 * @param {string} [trigger-label-mode] - The format of the trigger label display.
 * @param {string} [hover-date-format] - The format of label on hover.
 * @param {string} [locale] - The locale/language that the custom date range picker will use
 * @param {string} [timezone] - The timezone to be used by the custom date range picker. Valid momentjs
 *    timezones (see https://momentjs.com/timezone/docs/#/using-timezones/parsing-in-zone/) are supported.  If not specified, the timezone of the browser will be used.
 * @param {Object} [custom-range-picker-options] - custom options related to the custom date-range picker, includes locale, start-date, end-date, min-view, max-view, min-date, max-date, max-days
 * @param {Object} [custom-range-dialog-options] - custom options related to the dialog of the custom date range picker
 * @param {boolean} [compact-mode] - Control if the icon for the trigger button is showed or not
 * @param {boolean} [limit-end-to-now=false] - If true, future date/time is not allowed and the end date time will automtically be set to 'now' (the moment the user selects the date range)
 * @param {expression} [on-select] - Angular expression to evaluate on select of a date range menu item. $event is the DateRangeOption object representing the selected date range option.
 *
 * @description
 * `<mw-date-range-menu>` provides a trigger button and dropdown menu for selecting from a list of common date ranges.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-date-range-menu date-range="$ctrl.selectedDateRange" on-select="$ctrl.selectedDateRange = $event"></mw-date-range-menu>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-date-range-menu',
    template: `
        <md-menu class="mw-menu mw-date-range-menu" ng-if="!$ctrl.icon" md-position-mode="target-left target" md-offset="0 50">
            <mw-menu-trigger-button class="mw-date-range-menu__trigger-button" tooltip="{{'Select date range' | translate}}" on-click="$mdMenu.open($event)">
                <mw-icon ng-if="$ctrl.showIcon" svg-icon="action:ic_date_range_24px"></mw-icon>
                <span> {{ $ctrl.triggerLabel }} </span>
            </mw-menu-trigger-button>

            <md-menu-content>
                <mw-date-range-selector
                    relative-date="$ctrl.relativeDate"
                    date-range="$ctrl.dateRange"
                    date-range-types="$ctrl.dateRangeTypes"
                    on-select="$ctrl.onSelectHandler($event)"
                    hover-date-format="$ctrl.hoverDateFormat"
                    no-padding="true"
                    custom-range-dialog-options="$ctrl.customRangeDialogOptions"
                    custom-range-picker-options="$ctrl.customRangePickerOptions"
                    locale="$ctrl.locale"
                    timezone="$ctrl.timezone"
                    limit-end-to-now="$ctrl.limitEndToNow"
                    >
                </mw-date-range-selector>
            </md-menu-content>
        </md-menu>

        <md-menu class="mw-menu mw-date-range-menu" ng-if="$ctrl.icon" md-position-mode="target-left target" md-offset="0 50">
            <mw-menu-trigger-icon-button icon="{{ $ctrl.icon }}" tooltip="{{'Select date range' | translate}}" on-click="$mdMenu.open($event)"> </mw-menu-trigger-icon-button>

            <md-menu-content>
                <mw-date-range-selector
                    relative-date="$ctrl.relativeDate"
                    date-range="$ctrl.dateRange"
                    date-range-types="$ctrl.dateRangeTypes"
                    on-select="$ctrl.onSelectHandler($event)"
                    hover-date-format="$ctrl.hoverDateFormat"
                    no-padding="true"
                    custom-range-dialog-options="$ctrl.customRangeDialogOptions"
                    custom-range-picker-options="$ctrl.customRangePickerOptions"
                    locale="$ctrl.locale"
                    timezone="$ctrl.timezone"
                    limit-end-to-now="$ctrl.limitEndToNow"
                    >
                </mw-date-range-selector>
            </md-menu-content>
        </md-menu>
    `
})
export class DateRangeMenuComponent implements OnInit {
    @Input('<') compactMode: boolean = false;
    @Input('<') customRangeDialogOptions = {};
    @Input('<') customRangePickerOptions = {};
    @Input('<') dateRange: DateRange;
    @Input('<') dateRangeTypes: Array<DateRangeType> = new Array<DateRangeType>();
    @Input('<') hoverDateFormat: String;
    @Input('<') relativeDate: Date;
    @Input('<') showIcon: boolean = false;
    @Input('<') triggerDateFormat: String = 'MMM D';
    @Input('<') triggerDateFormatWithYear: String = 'MMM D, YYYY';
    @Input('<') triggerLabelMode: ('date'|'name') = 'name';
    @Input('<') locale: String;
    @Input('<') timezone: string;
    @Input('<') limitEndToNow: boolean;
    @Output() onSelect: EventEmitter<DateRange> = new EventEmitter<DateRange>();
    private mwDateRangePipe;

    get icon(): string {
        return this.compactMode ? 'action:ic_date_range_24px' : null;
    }

    get triggerLabel(): string {
        if (this.dateRange) {
            if (this.triggerLabelMode === 'name' && this.dateRange.name && this.dateRange.name !== 'Custom') {
                return this.$translate.instant(this.dateRange.name);
            } else {
                let thisYear = (new Date()).getFullYear();
                let dateFormat = ((new Date(this.dateRange.startDate)).getFullYear() !== thisYear || (new Date(this.dateRange.endDate)).getFullYear() !== thisYear) ? this.triggerDateFormatWithYear : this.triggerDateFormat;
                return this.mwDateRangePipe(this.dateRange, dateFormat, this.timezone, this.locale);
            }
        } else {
            return '';
        }
    }

    constructor(
        @Inject('$mdMedia') private $mdMedia: ng.material.IMedia,
        @Inject('$scope') private $scope,
        @Inject('$filter') private $filter: ng.IFilterService,
        @Inject('$translate') private $translate: ng.translate.ITranslateService) {
        'ngInject';

        this.mwDateRangePipe = this.$filter('mwDateRange');
    }

    public ngOnInit() {
        this.locale = this.locale ? this.locale : this.$translate.use();
        this.timezone = this.timezone ? this.timezone : momentTz.tz.guess(); // If no timezone is passed in, let moment guess the timezone

        this.$scope.$on('$mdMenuOpen', (event, menu) => {
            const today = momentTz().tz(this.timezone).format('YYYY-MM-DD');
            const relative = momentTz(this.relativeDate).tz(this.timezone).format('YYYY-MM-DD');
            this.relativeDate = today === relative ? momentTz().tz(this.timezone).toDate() : momentTz(this.relativeDate).tz(this.timezone).toDate();
        });
    }

    private onSelectHandler(dateRange: DateRange) {
        this.onSelect.emit(dateRange);
    }

}



// WEBPACK FOOTER //
// ./src/lib/components/date-range/date-range-menu/date-range-menu.component.ts